/**
 * Order-page payable total display — passthrough from Backend financial SSOT.
 * @see ldsBackendFinancialSsot.js
 */
import { resolveLdsOrderFinalPriceBreakdown } from "../extractLdsOrderFinalPriceIls.js";
import { resolveLdsBackendFinancialSsot } from "./ldsBackendFinancialSsot.js";

export const LDS_ORDER_TOTAL_SSOT_TRACE_TAG = "[LDS_ORDER_TOTAL_SSOT_TRACE]";
export const LDS_FE_TOTAL_SOURCE_TRACE_TAG = "[LDS_FE_TOTAL_SOURCE_TRACE]";

function positive(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

/**
 * @param {object|null|undefined} order
 * @param {{ emitTrace?: boolean, totalAmountOverride?: number|null }} [options]
 */
export function resolveLdsOrderPageDisplayTotalSsot(order, options = {}) {
  const empty = {
    displayedTotal: null,
    source: "none",
    baggage_added_once: false,
    base_before_baggage: null,
    customer_total: null,
    pricing_snapshot_total: null,
  };

  if (!order || typeof order !== "object") {
    return empty;
  }

  const breakdown = resolveLdsOrderFinalPriceBreakdown(order);
  const backend = resolveLdsBackendFinancialSsot(order, {
    emitTrace: false,
    totalAmountOverride: options?.totalAmountOverride,
  });

  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const snap = op?.pricingSnapshot ?? op?.pricing_snapshot ?? {};

  const out = {
    displayedTotal: backend.total,
    source: backend.totalSource,
    baggage_added_once: Boolean(breakdown.baggageAddedOnce),
    base_before_baggage: breakdown.baseBeforeBaggage,
    customer_total: positive(order?.customer_total ?? order?.customerTotal),
    pricing_snapshot_total: positive(snap?.final_price ?? snap?.finalPrice),
  };

  const bags = Number(breakdown.baggageTotalDetected) || 0;
  const backendTotal = positive(backend.total);
  const composed = positive(breakdown.finalTotal);
  if (
    bags > 0 &&
    backendTotal != null &&
    composed != null &&
    composed > backendTotal + 0.02 &&
    Boolean(breakdown.baggageAddedOnce)
  ) {
    out.displayedTotal = composed;
    out.source = `${backend.totalSource || "backend"} + baggage`;
  }

  if (options.emitTrace !== false && isDevRuntime()) {
    try {
      // eslint-disable-next-line no-console
      console.info(LDS_ORDER_TOTAL_SSOT_TRACE_TAG, {
        orderId: String(order.id ?? order._id ?? order.orderid ?? ""),
        displayed_total: out.displayedTotal,
        source: out.source,
        hasBackendBlock: backend.hasBackendBlock,
        rejectedCandidates: backend.rejectedCandidates,
      });
    } catch {
      /* ignore */
    }
  }

  return out;
}

/** @deprecated Use buildLdsFeFinalFinancialSsotTracePayload from ldsBackendFinancialSsot.js */
export function buildLdsFeTotalSourceTracePayload(order, financial, pageSsot, partialBal = {}) {
  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const beUpdated = order?.lds_updated_order_total ?? order?.ldsUpdatedOrderTotal;
  const bePartial = order?.lds_partial_payment_display ?? order?.ldsPartialPaymentDisplay;
  return {
    orderId: String(order?.id ?? order?._id ?? order?.orderid ?? ""),
    displayedTotal: financial?.totalAmount ?? pageSsot?.displayedTotal ?? null,
    displayedPaid: financial?.paidAmount ?? null,
    displayedRemaining: financial?.remainingBalance ?? null,
    source: pageSsot?.source ?? financial?.debug?.sourceUsed ?? null,
    ldsUpdatedOrderTotal: beUpdated?.display_total_updated ?? null,
    partialPaymentDisplayTotal: bePartial?.display_total ?? null,
    priceWithBaggageFare: op.priceWithBaggageFare ?? null,
    baggageFee:
      partialBal?.baggageFee ??
      op.baggagePrice ??
      order?.baggagePrice ??
      null,
    customerTotal: order?.customer_total ?? order?.customerTotal ?? null,
    rawCandidates: partialBal?.rawTotalCandidates ?? [],
  };
}

/** @deprecated Use emitLdsFeFinalFinancialSsotTrace */
export function emitLdsFeTotalSourceTrace(payload) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info(LDS_FE_TOTAL_SOURCE_TRACE_TAG, payload);
  } catch {
    /* ignore */
  }
}
